/**
 * The holiday garland draped over the poster greeting (Bobbie, looking at
 * the season preview: "Can you do the lights and spiderwebs on the let's as
 * well?"). One small piece of art per holiday, hung over the top edge of
 * "Let's / Go, {name}" - a spiderweb tucked in the corner for Halloween, a
 * string of lights for Christmas, a heart garland for Valentine's, and a
 * scatter for the other two so all five holidays read as one system.
 *
 * Purely decorative: aria-hidden, no pointer events, absolutely positioned
 * so it never affects the heading's layout or its accessible name. Mount by
 * wrapping the poster <h1> with position: relative and rendering
 * <SeasonHeaderDecoration seasonKey={season.key} /> as a child.
 *
 * `tone` matters more than it looks like it should (review finding: the
 * first pass hardcoded a dark ink stroke and it vanished on every mount
 * that sits on a dark surface). Two of the three real mounts - the family
 * and dancer home heroes - are NOT the app's light/dark theme; they are
 * commandCenter/styles.ts's own always-dark ink band, in both themes. So:
 *   'onLight' (default) - the landing greeting and /season-preview, both
 *     genuinely light/dark theme-reactive. Strokes read var(--ink-ghost),
 *     which already flips light-ink-on-light-card to light-text-on-dark-
 *     card by itself - no new token needed.
 *   'onDark' - the family and dancer heroes. A literal light stroke, the
 *     same color family as the hero's own INK_BRD/INK_TX3
 *     (commandCenter/styles.ts), at an alpha strong enough for a thin line
 *     to actually read (INK_BRD's own 0.18 all but disappears at 1.2px).
 */

interface ToneProps {
  tone?: 'onLight' | 'onDark';
}

function strokeFor(tone: 'onLight' | 'onDark' | undefined): string {
  return tone === 'onDark' ? 'rgba(244, 247, 242, 0.4)' : 'var(--ink-ghost)';
}

const wrapStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  overflow: 'visible',
  pointerEvents: 'none',
};

/**
 * A simple 4-spoke corner web. Anchored with a percentage left offset tuned
 * against the app's mobile-first primary viewport so it lands near the
 * actual end of "LET'S" rather than the far right edge of the full-width
 * heading box - necessarily an approximation across viewport widths (the
 * heading has no per-word wrapper to anchor against precisely), good
 * enough to read as "on the word" rather than "in the corner of the page".
 */
function HalloweenWeb({ tone }: ToneProps) {
  const stroke = strokeFor(tone);
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 60 55"
      style={{
        position: 'absolute',
        top: -10,
        left: '46%',
        width: 40,
        height: 36,
        pointerEvents: 'none',
      }}
    >
      <g fill="none" stroke={stroke} strokeWidth="1.2" strokeLinecap="round">
        <path d="M58,2 L58,50" />
        <path d="M58,2 L38,46" />
        <path d="M58,2 L16,34" />
        <path d="M58,2 L2,14" />
        <path d="M58,21 L50,20 L41,15 L36,7" />
        <path d="M58,38 L43,35 L27,26 L16,11" />
      </g>
    </svg>
  );
}

/** A wavy string with six bulbs, alternating red, green, and gold. */
function ChristmasLights({ tone }: ToneProps) {
  const stroke = strokeFor(tone);
  const bulbs: Array<{ x: number; y: number; color: string }> = [
    { x: 12, y: 11, color: '#d92d2d' },
    { x: 58, y: 23, color: '#40d11f' },
    { x: 104, y: 11, color: '#ffd400' },
    { x: 150, y: 23, color: '#d92d2d' },
    { x: 196, y: 11, color: '#40d11f' },
    { x: 236, y: 23, color: '#ffd400' },
  ];
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 250 34"
      preserveAspectRatio="none"
      style={{ position: 'absolute', top: -16, left: 0, width: '100%', height: 30, pointerEvents: 'none' }}
    >
      <path
        d="M4,8 Q35,28 58,20 Q85,4 104,8 Q135,28 150,20 Q177,4 196,8 Q227,28 246,20"
        fill="none"
        stroke={stroke}
        strokeWidth="1.4"
      />
      {bulbs.map((b, i) => (
        <g key={i}>
          <circle cx={b.x} cy={b.y} r={4.5} fill={b.color} opacity={0.28} />
          <circle cx={b.x} cy={b.y} r={2.6} fill={b.color} />
        </g>
      ))}
    </svg>
  );
}

/**
 * A single gentle dip strung with five small hearts, red and pink.
 *
 * Review finding (BOUNCE): the per-heart hang formula used a control point
 * (120,46) that did not match the path actually drawn (120,26), so every
 * heart floated below the string with a gap instead of hanging from it, and
 * the middle three clipped against the viewBox. STRING_CONTROL below is now
 * the SAME point the <path> element uses - one source of truth - and
 * stringYAt() inverts x(t) exactly rather than approximating it (the three
 * control points are evenly spaced in x, so x(t) is exactly linear; t =
 * (x - start.x) / (end.x - start.x) is exact, not an approximation).
 */
function ValentineGarland({ tone }: ToneProps) {
  const stroke = strokeFor(tone);
  const STRING_START = { x: 4, y: 6 };
  const STRING_CONTROL = { x: 120, y: 26 };
  const STRING_END = { x: 236, y: 6 };
  const hearts: Array<{ x: number; color: string }> = [
    { x: 20, color: '#d92d2d' },
    { x: 70, color: '#e0357f' },
    { x: 120, color: '#d92d2d' },
    { x: 170, color: '#e0357f' },
    { x: 220, color: '#d92d2d' },
  ];
  const stringYAt = (x: number) => {
    const t = (x - STRING_START.x) / (STRING_END.x - STRING_START.x);
    const mt = 1 - t;
    return mt * mt * STRING_START.y + 2 * mt * t * STRING_CONTROL.y + t * t * STRING_END.y;
  };
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 240 42"
      preserveAspectRatio="none"
      style={{ position: 'absolute', top: -16, left: 0, width: '100%', height: 36, pointerEvents: 'none' }}
    >
      <path
        d={`M${STRING_START.x},${STRING_START.y} Q${STRING_CONTROL.x},${STRING_CONTROL.y} ${STRING_END.x},${STRING_END.y}`}
        fill="none"
        stroke={stroke}
        strokeWidth="1.4"
      />
      {hearts.map((h, i) => {
        const y = stringYAt(h.x);
        return (
          <g key={i} transform={`translate(${h.x}, ${y})`}>
            <line x1="0" y1="0" x2="0" y2="4" stroke={stroke} strokeWidth="1" />
            <path
              d="M4,14 C1,11 -3,7.5 0,4.5 C1.6,3 4,3 4,5.5 C4,3 6.4,3 8,4.5 C11,7.5 7,11 4,14 Z"
              fill={h.color}
            />
          </g>
        );
      })}
    </svg>
  );
}

/** A scatter of leaf or clover emoji, tastefully small and lightly rotated. */
function EmojiScatter({ glyphs }: { glyphs: Array<{ char: string; top: number; leftPct: number; size: number; rotate: number }> }) {
  return (
    <div aria-hidden="true" style={{ ...wrapStyle, userSelect: 'none' }}>
      {glyphs.map((g, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            top: g.top,
            left: `${g.leftPct}%`,
            fontSize: g.size,
            lineHeight: 1,
            transform: `rotate(${g.rotate}deg)`,
          }}
        >
          {g.char}
        </span>
      ))}
    </div>
  );
}

const THANKSGIVING_LEAVES = [
  { char: '🍁', top: -14, leftPct: 4, size: 20, rotate: -18 },
  { char: '🍂', top: -6, leftPct: 34, size: 15, rotate: 14 },
  { char: '🍁', top: -16, leftPct: 66, size: 17, rotate: 22 },
];

const STPATRICKS_CLOVERS = [
  { char: '🍀', top: -12, leftPct: 6, size: 18, rotate: -12 },
  { char: '🍀', top: -8, leftPct: 58, size: 15, rotate: 16 },
];

export function SeasonHeaderDecoration({ seasonKey, tone = 'onLight' }: { seasonKey: string } & ToneProps) {
  switch (seasonKey) {
    case 'halloween':
      return <HalloweenWeb tone={tone} />;
    case 'christmas':
      return <ChristmasLights tone={tone} />;
    case 'valentines':
      return <ValentineGarland tone={tone} />;
    case 'thanksgiving':
      return <EmojiScatter glyphs={THANKSGIVING_LEAVES} />;
    case 'stpatricks':
      return <EmojiScatter glyphs={STPATRICKS_CLOVERS} />;
    default:
      return null;
  }
}

export default SeasonHeaderDecoration;
